import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

/**
 * Cross-feature UI state.
 *
 * Only state that genuinely spans features lives here: the AI drawer can be
 * opened from the sidebar, the dashboard and any attention card. Everything
 * else stays local to its component or in the query cache.
 */
interface UiState {
  aiDrawerOpen: boolean;
  /** Question pre-filled when the drawer is opened from a specific context. */
  aiDrawerSeed: string | null;
  openAiDrawer: (seed?: string) => void;
  closeAiDrawer: () => void;

  mobileNavOpen: boolean;
  toggleMobileNav: () => void;
  closeMobileNav: () => void;
}

const UiContext = createContext<UiState | null>(null);

export function UiProvider({ children }: { children: ReactNode }) {
  const [aiDrawerOpen, setAiDrawerOpen] = useState(false);
  const [aiDrawerSeed, setAiDrawerSeed] = useState<string | null>(null);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const openAiDrawer = useCallback((seed?: string) => {
    setAiDrawerSeed(seed ?? null);
    setAiDrawerOpen(true);
    setMobileNavOpen(false);
  }, []);

  const closeAiDrawer = useCallback(() => {
    setAiDrawerOpen(false);
    setAiDrawerSeed(null);
  }, []);

  const toggleMobileNav = useCallback(() => setMobileNavOpen((open) => !open), []);
  const closeMobileNav = useCallback(() => setMobileNavOpen(false), []);

  const value = useMemo(
    () => ({
      aiDrawerOpen,
      aiDrawerSeed,
      openAiDrawer,
      closeAiDrawer,
      mobileNavOpen,
      toggleMobileNav,
      closeMobileNav,
    }),
    [
      aiDrawerOpen,
      aiDrawerSeed,
      openAiDrawer,
      closeAiDrawer,
      mobileNavOpen,
      toggleMobileNav,
      closeMobileNav,
    ],
  );

  return <UiContext.Provider value={value}>{children}</UiContext.Provider>;
}

export function useUi(): UiState {
  const context = useContext(UiContext);
  if (!context) throw new Error('useUi must be used inside a UiProvider');
  return context;
}
